import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAdminToken } from "@/context/AdminTokenContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  title: "",
  excerpt: "",
  body: "",
  author: "The Graceful Guidance Team",
  cover_image_url: "",
  tags: "",
};

export default function AdminBlogPage() {
  const { token, setToken } = useAdminToken();
  const [form, setForm] = useState(EMPTY);
  const [posts, setPosts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadPosts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/blog?limit=100`);
      setPosts(data || []);
    } catch {
      setPosts([]);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Enter your admin token first.");
      return;
    }
    if (!form.title || !form.excerpt || !form.body) {
      toast.error("Title, excerpt and body are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        body: form.body,
        author: form.author.trim() || "The Graceful Guidance Team",
        cover_image_url: form.cover_image_url.trim() || null,
        tags: form.tags
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : null,
      };
      await axios.post(`${API}/blog`, payload, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Post published. Subscribers are being notified.");
      setForm(EMPTY);
      loadPosts();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to publish.";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    if (!token) return toast.error("Enter admin token first.");
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/blog/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Post deleted.");
      loadPosts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="bg-[var(--gg-surface)] min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-sm text-[var(--gg-text-muted)] hover:text-[var(--gg-primary)] mb-6"
        >
          <ArrowLeft size={16} /> Admin hub
        </Link>
        <h1 className="font-serif text-4xl text-[var(--gg-deep)] font-semibold">
          Graceful Blog — Admin
        </h1>
        <p className="text-[var(--gg-text)] mt-2">
          Publish a new post. All active subscribers will receive it by email
          (once <code>RESEND_API_KEY</code> is configured on the server).
        </p>

        {/* Admin token */}
        <div className="mt-8 bg-white border border-[var(--gg-border)] rounded-2xl p-6">
          <Label className="text-xs uppercase tracking-[0.18em] text-[var(--gg-text-muted)] font-semibold">
            Admin token
          </Label>
          <div className="mt-2">
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your X-Admin-Token"
              data-testid="admin-token-input"
            />
          </div>
          <p className="text-xs text-[var(--gg-text-muted)] mt-2">
            Held in memory only — never stored in the browser. Tip: unlock once
            at the <Link to="/admin" className="text-[var(--gg-primary)] underline">admin hub</Link> to
            share it across all admin pages.
          </p>
        </div>

        {/* New post form */}
        <form
          onSubmit={onSubmit}
          className="mt-8 bg-white border border-[var(--gg-border)] rounded-2xl p-6 space-y-5"
          data-testid="admin-blog-form"
        >
          <h2 className="font-serif text-2xl text-[var(--gg-deep)] font-semibold">
            New post
          </h2>
          <FormField label="Title">
            <Input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              data-testid="blog-form-title"
              required
            />
          </FormField>
          <FormField label="Excerpt (short summary)">
            <Textarea
              value={form.excerpt}
              onChange={(e) => update("excerpt", e.target.value)}
              rows={2}
              data-testid="blog-form-excerpt"
              required
            />
          </FormField>
          <FormField label="Body">
            <Textarea
              value={form.body}
              onChange={(e) => update("body", e.target.value)}
              rows={12}
              data-testid="blog-form-body"
              required
            />
          </FormField>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField label="Author">
              <Input
                value={form.author}
                onChange={(e) => update("author", e.target.value)}
                data-testid="blog-form-author"
              />
            </FormField>
            <FormField label="Cover image URL (optional)">
              <Input
                value={form.cover_image_url}
                onChange={(e) => update("cover_image_url", e.target.value)}
                data-testid="blog-form-cover"
              />
            </FormField>
          </div>
          <FormField label="Tags (comma separated)">
            <Input
              value={form.tags}
              onChange={(e) => update("tags", e.target.value)}
              data-testid="blog-form-tags"
              placeholder="care, relocation, diaspora"
            />
          </FormField>
          <button
            type="submit"
            disabled={submitting}
            data-testid="blog-form-submit"
            className="bg-[var(--gg-primary)] hover:bg-[var(--gg-primary-dark)] disabled:opacity-60 text-white rounded-full px-7 py-3 text-sm"
          >
            {submitting ? "Publishing…" : "Publish & notify subscribers"}
          </button>
        </form>

        {/* Existing posts */}
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-[var(--gg-deep)] font-semibold">
            Published posts
          </h2>
          <ul className="mt-4 divide-y divide-[var(--gg-border)] bg-white rounded-2xl border border-[var(--gg-border)]">
            {posts.length === 0 && (
              <li className="p-6 text-sm text-[var(--gg-text-muted)]">
                No posts yet.
              </li>
            )}
            {posts.map((p) => (
              <li
                key={p.id}
                className="p-5 flex items-start justify-between gap-4"
                data-testid={`admin-post-${p.id}`}
              >
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--gg-primary)] font-semibold">
                    {new Date(p.created_at).toLocaleDateString()}
                  </div>
                  <div className="font-serif text-lg text-[var(--gg-deep)] mt-1 truncate">
                    {p.title}
                  </div>
                  <div className="text-sm text-[var(--gg-text-muted)] mt-1 truncate">
                    {p.excerpt}
                  </div>
                </div>
                <button
                  onClick={() => onDelete(p.id)}
                  data-testid={`admin-delete-${p.id}`}
                  className="text-[var(--gg-text-muted)] hover:text-red-600 p-2"
                  aria-label="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.18em] text-[var(--gg-text-muted)] font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
